import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { toast } from 'sonner'
import { Camera } from 'lucide-react'
import { useAuthStore } from '@/application/stores/auth.store'
import { useGetMe, useUpdateMe } from '@/application/services/user.service'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/ui/components/common/Card'
import { Button } from '@/ui/components/common/Button'
import { Input, Textarea } from '@/ui/components/forms/Input'
import { ImageField } from '@/ui/components/forms/ImageField'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/ui/components/forms/Form'

const makeProfileSchema = (_translate: (k: string) => string) =>
  z.object({
    displayName: z.string().max(100).optional().or(z.literal('')),
    firstName: z.string().max(100).optional().or(z.literal('')),
    lastName: z.string().max(100).optional().or(z.literal('')),
    email: z.string().email().optional().or(z.literal('')),
    avatarUrl: z.string().max(2048).optional().or(z.literal('')),
    biography: z.string().max(140).optional().or(z.literal('')),
  })

type ProfileFormValues = z.infer<ReturnType<typeof makeProfileSchema>>

/**
 * Account information section: read-only username/role plus an editable form
 * for avatar, display name, first/last name, and bio. Owns its own form state,
 * fetches the current profile, and submits updates while syncing the auth store.
 */
export function ProfileInfoSection() {
  const { t } = useTranslation()
  const { user, setUser } = useAuthStore()
  const { data: profile } = useGetMe()
  const { mutateAsync: updateMe, isPending } = useUpdateMe()

  const schema = makeProfileSchema(t)
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      displayName: '',
      firstName: '',
      lastName: '',
      email: '',
      avatarUrl: '',
      biography: '',
    },
  })

  useEffect(() => {
    if (profile) {
      form.reset({
        displayName: profile.displayName ?? '',
        firstName: profile.firstName ?? '',
        lastName: profile.lastName ?? '',
        email: profile.email ?? '',
        avatarUrl: profile.avatarUrl ?? '',
        biography: profile.biography ?? '',
      })
    }
  }, [profile, form])

  const handleSubmit = async (values: ProfileFormValues) => {
    try {
      const updated = await updateMe({
        ...values,
        email: values.email || null,
        avatarUrl: values.avatarUrl || null,
        biography: values.biography || null,
      })
      if (user && updated) {
        setUser({ ...user, displayName: updated.displayName ?? user.displayName })
      }
      toast.success(t('admin.profile_save_success'))
    } catch (httpError: unknown) {
      const isConflict =
        httpError !== null &&
        typeof httpError === 'object' &&
        'response' in httpError &&
        httpError.response !== null &&
        typeof httpError.response === 'object' &&
        'status' in httpError.response &&
        httpError.response.status === 409
      toast.error(isConflict ? t('admin.profile_email_conflict') : t('admin.profile_save_error'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.profile_info_title')}</CardTitle>
        <CardDescription>{t('admin.profile_info_desc')}</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Read-only info */}
        <div className="mb-6 space-y-2">
          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
            <span className="text-sm font-medium text-muted-foreground w-32">
              {t('admin.profile_username')}
            </span>
            <span className="text-sm font-mono">{profile?.username ?? user?.username ?? '—'}</span>
          </div>
          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
            <span className="text-sm font-medium text-muted-foreground w-32">
              {t('admin.profile_role')}
            </span>
            <span className="text-sm capitalize">{profile?.role ?? user?.role ?? '—'}</span>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Avatar */}
            <FormField
              control={form.control}
              name="avatarUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.profile_avatar_label')}</FormLabel>
                  <FormControl>
                    <div className="flex items-start gap-6">
                      {/* Circular preview */}
                      <div className="shrink-0">
                        <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-border bg-muted flex items-center justify-center">
                          {field.value ? (
                            <img
                              src={field.value}
                              alt="Avatar"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Camera className="w-8 h-8 text-muted-foreground/40" />
                          )}
                        </div>
                      </div>
                      {/* ImageField for upload/URL */}
                      <div className="flex-1">
                        <ImageField
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          disabled={isPending}
                        />
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.profile_display_name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('admin.profile_display_name_placeholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.profile_first_name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('admin.profile_first_name_placeholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.profile_last_name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('admin.profile_last_name_placeholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.profile_email')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t('admin.profile_email_placeholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Bio */}
            <FormField
              control={form.control}
              name="biography"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('admin.profile_bio_label')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('admin.profile_bio_placeholder')}
                      maxLength={140}
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <div className="flex justify-end">
                    <span className="text-xs text-muted-foreground">
                      {(field.value ?? '').length}/140
                    </span>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end pt-2">
              <Button type="submit" isLoading={isPending}>
                {t('admin.profile_save')}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
